import { Loader2, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DateInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { formatFileSize } from '@/utils/formatters';

interface InvestorVisibilityUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  files: File[];
  /** Rejection reasons from the last attempt, keyed by file name. */
  fileErrors?: Record<string, string>;
  visibilityLabel: string;
  visibilityDescription: string;
  investorVisible: boolean;
  onInvestorVisibleChange: (visible: boolean) => void;
  /** yyyy-mm-dd; empty falls back to the upload date. */
  asOf: string;
  onAsOfChange: (asOf: string) => void;
  onAddFiles: (files: FileList | null) => void;
  onRemoveFile: (fileName: string) => void;
  onUpload: () => void;
  isUploading: boolean;
}

export function InvestorVisibilityUploadDialog({
  open,
  onOpenChange,
  files,
  fileErrors,
  visibilityLabel,
  visibilityDescription,
  investorVisible,
  onInvestorVisibleChange,
  asOf,
  onAsOfChange,
  onAddFiles,
  onRemoveFile,
  onUpload,
  isUploading,
}: InvestorVisibilityUploadDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Documents</DialogTitle>
          <DialogDescription>
            {files.length} file(s) selected for upload.
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0 py-4 space-y-4">
          <div className="space-y-1">
            <Label htmlFor="upload-as-of">As of date</Label>
            <DateInput
              id="upload-as-of"
              className="w-44"
              value={asOf}
              onChange={onAsOfChange}
            />
            <p className="text-xs text-muted-foreground">
              Date the documents are about. Empty uses the upload date.
            </p>
          </div>
          <div className="flex items-center justify-between gap-4 p-4 bg-muted rounded-lg">
            <div className="space-y-0.5">
              <Label htmlFor="investor-visible" className="text-sm font-medium">
                {visibilityLabel}
              </Label>
              <p className="text-xs text-muted-foreground">
                {visibilityDescription}
              </p>
            </div>
            <Switch
              id="investor-visible"
              checked={investorVisible}
              onCheckedChange={onInvestorVisibleChange}
            />
          </div>
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-2">Files to upload:</p>
            <ul className="space-y-1 max-h-32 overflow-y-auto">
              {files.map((file) => (
                <li key={file.name}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate">
                      {file.name} ({formatFileSize(file.size)})
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 shrink-0"
                      onClick={() => onRemoveFile(file.name)}
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  {fileErrors?.[file.name] && (
                    <p className="text-xs text-destructive">
                      {fileErrors[file.name]}
                    </p>
                  )}
                </li>
              ))}
            </ul>
            <Label
              htmlFor="add-upload-files"
              className="mt-2 inline-block cursor-pointer text-xs underline"
            >
              Add files
            </Label>
            <Input
              id="add-upload-files"
              type="file"
              multiple
              className="hidden"
              onChange={(event) => {
                onAddFiles(event.target.files);
                // Allow re-selecting the same file after removing it.
                event.target.value = '';
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onUpload}
            disabled={isUploading || files.length === 0}
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
